import { useEffect, useMemo, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import useCompany from "../../../hooks/useCompany";
import { Button } from "@oc-labs/wt-storybook";
import toast from "react-hot-toast";
import {
  ArrowLeftRight,
  Clipboard,
  Coins,
  Globe,
  LayoutGrid,
  Network,
  Package,
  Settings,
  Users,
} from "lucide-react";
import useNetworks from "../../../hooks/useNetworks";

const TABS_CONFIG = [
  {
    key: "Tokens",
    title: "Tokens",
    description: "Show balances and token detail screens in the wallet.",
    icon: Coins,
  },
  {
    key: "Assets",
    title: "Assets",
    description: "Expose RWA and asset modules in the main navigation.",
    icon: Package,
  },
  {
    key: "Transactions",
    title: "Transactions",
    description: "Enable movements history and transaction details.",
    icon: ArrowLeftRight,
  },
  {
    key: "Members",
    title: "Members",
    description: "Allow members section for account management.",
    icon: Users,
  },
];

export default function WalletConfiguration() {
  const { user } = useAuth();
  const { activeCompany, getCompanyDetails, updateCompany } = useCompany();
  const { networks, loadNetworks } = useNetworks();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [registrationTemplateId, setRegistrationTemplateId] = useState("");
  const [domain, setDomain] = useState("");

  useEffect(() => {
    if (activeCompany?.id) {
      getCompanyDetails(activeCompany.id).then((org) => {
        const normalized = {
          ...org,
          tabsEnabled: org?.tabsEnabled || [],
          accentTextColor: org?.accentTextColor || "#000000",
          primaryFont: org?.primaryFont || "",
          secondaryFont: org?.secondaryFont || "",
          iframeBackgroundOpacity: org?.iframeBackgroundOpacity ?? 1,
          recoveryPasswordTemplateId: org?.recoveryPasswordTemplateId || "",
          pinTemplateId: org?.pinTemplateId || "",
        };
        setOrganizationSafe(normalized);
        setRegistrationTemplateId(org?.registrationTemplateId || "");
        setDomain(org?.domain || "");
        setLoading(false);
      });
    }
  }, [activeCompany]);

  const isManager = user?.role === "admin";

  const setOrganizationSafe = (data) => {
    const normalized = {
      ...data,
      primaryFont: String(data?.primaryFont || ""),
      secondaryFont: String(data?.secondaryFont || ""),
      iframeBackgroundOpacity: Number(data?.iframeBackgroundOpacity) || 1,
      recoveryPasswordTemplateId: String(data?.recoveryPasswordTemplateId || ""),
      pinTemplateId: String(data?.pinTemplateId || ""),
      accentTextColor: String(data?.accentTextColor || "#000000"),
    };
    setOrganization(normalized);
  };

  const safeUpdateCompany = async (companyId, updateData) => {
    const baseData = {
      primaryFont: String(organization?.primaryFont || ""),
      secondaryFont: String(organization?.secondaryFont || ""),
      iframeBackgroundOpacity: Number(organization?.iframeBackgroundOpacity) || 1,
      recoveryPasswordTemplateId: String(organization?.recoveryPasswordTemplateId || ""),
      pinTemplateId: String(organization?.pinTemplateId || ""),
      accentTextColor: String(organization?.accentTextColor || "#000000"),
    };

    const dataToSend = {
      ...baseData,
      ...Object.fromEntries(
        Object.entries(updateData).map(([key, value]) => [
          key,
          value !== null && value !== undefined ? value : baseData[key],
        ]),
      ),
    };

    return await updateCompany(companyId, dataToSend);
  };

  useEffect(() => {
    loadNetworks()
  }, []);

  const activeNetworkName = useMemo(() => {
    return networks.find((network) => network.id === organization?.defaultNetwork)?.name || "Not selected";
  }, [networks, organization?.defaultNetwork]);

  if (loading || !activeCompany || !organization) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div>Loading...</div>
      </div>
    );
  }

  const handleSwitchTab = async (tabName, isEnabled) => {
    const updatedTabs = isEnabled
      ? organization.tabsEnabled.filter((tab) => tab !== tabName)
      : [...(organization.tabsEnabled || []), tabName];

    const result = await safeUpdateCompany(activeCompany.id, { tabsEnabled: updatedTabs });
    if (result?.error) {
      toast.error(result.error.message || "You are not allowed to update this organization");
      return;
    }
    setOrganizationSafe({ ...organization, tabsEnabled: updatedTabs });
    toast.success(`"${tabName}" tab ${isEnabled ? "disabled" : "enabled"}`);
  };

  return (
    <div className="2xl:p-6 p-3 2xl:space-y-6 space-y-4">
      <section className="relative overflow-hidden rounded-2xl border border-[#1d2498] bg-gradient-to-br from-[#1f299f] via-[#2B36D9] to-[#1a228a] 2xl:p-6 p-4 text-white">
        <div className="absolute -right-12 -top-10 h-44 w-44 rounded-full bg-[#4c56eb]/35 blur-2xl" />
        <div className="absolute -left-8 -bottom-16 h-44 w-44 rounded-full bg-[#2B36D9]/35 blur-2xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-100">Wallet Studio</p>
            <h1 className="mt-2 2xl:text-3xl text-xl font-semibold">Configuration Console</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-200">
              Define default navigation, network behavior, templates, and module visibility for your organization wallet.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-white/20 bg-white/10 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-300">Default Network</div>
              <div className="mt-1 text-sm font-semibold">{activeNetworkName}</div>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-300">Default Tab</div>
              <div className="mt-1 text-sm font-semibold">{organization.defaultTabView || "Not selected"}</div>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/10 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-300">Enabled Tabs</div>
              <div className="mt-1 text-sm font-semibold">{organization.tabsEnabled?.length || 0}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-500" />
              General Settings
            </h2>
            <span className="text-xs font-medium text-gray-500">Applied immediately on save</span>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Default Network
              </label>
              <select
                className="w-full p-3 text-sm text-black bg-gray-50 border border-gray-200 rounded-xl outline-none transition-colors duration-300 focus:border-blue-400"
                value={organization.defaultNetwork || ""}
                onChange={async (e) => {
                  const result = await safeUpdateCompany(activeCompany.id, { defaultNetwork: e.target.value });
                  if (result?.error) {
                    toast.error(result.error.message || "You are not allowed to update this organization");
                    return;
                  }
                  setOrganizationSafe({ ...organization, defaultNetwork: e.target.value });
                  toast.success("Default network updated");
                }}
              >
                <option value="" disabled>
                  Select a network
                </option>
                {networks.map((network) => (
                  <option key={network.id} value={network.id}>
                    {network.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Default Tab View
              </label>
              <select
                className="w-full p-3 text-sm text-black bg-gray-50 border border-gray-200 rounded-xl outline-none transition-colors duration-300 focus:border-blue-400"
                value={organization.defaultTabView || ""}
                onChange={async (e) => {
                  const result = await safeUpdateCompany(activeCompany.id, { defaultTabView: e.target.value });
                  if (result?.error) {
                    toast.error(result.error.message || "You are not allowed to update this organization");
                    return;
                  }
                  setOrganizationSafe({ ...organization, defaultTabView: e.target.value });
                  toast.success("Default tab view updated");
                }}
              >
                <option value="" disabled>
                  Select a default tab view
                </option>
                <option value="Transactions">Transactions</option>
                <option value="Tokens">Tokens</option>
                <option value="Assets">Assets</option>
                <option value="Members">Members</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                <Globe className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                Domain
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="e.g. app.yourdomain.com"
                  className="flex-1 p-3 text-sm text-black bg-gray-50 border border-gray-200 rounded-xl outline-none transition-colors duration-300 focus:border-blue-400"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                />
                <Button
                  type="primary"
                  className="px-5 rounded-xl"
                  onClick={async () => {
                    const result = await safeUpdateCompany(activeCompany.id, { domain: domain || null });
                    if (result?.error) {
                      toast.error(result.error.message || "You are not allowed to update this organization");
                      return;
                    }
                    setOrganizationSafe({ ...organization, domain: domain || null });
                    toast.success("Domain updated");
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>

        {isManager && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clipboard className="w-4 h-4 text-gray-500" />
                Registration Template
              </h2>
              <p className="text-sm text-gray-500 mt-1">Set the default template used for new user registration.</p>
            </div>
            <div className="p-6 space-y-4">
              <input
                type="text"
                placeholder="Enter registration template name..."
                className="w-full p-3 text-sm text-black bg-gray-50 border border-gray-200 rounded-xl outline-none transition-colors duration-300 focus:border-blue-400"
                value={registrationTemplateId}
                onChange={(e) => setRegistrationTemplateId(e.target.value)}
              />
              <Button
                type="primary"
                className="w-full py-3 rounded-xl"
                onClick={async () => {
                  await safeUpdateCompany(activeCompany.id, {
                    registrationTemplateId: registrationTemplateId || null,
                  });
                  setOrganizationSafe({
                    ...organization,
                    registrationTemplateId: registrationTemplateId || null,
                  });
                  toast.success("Registration template updated");
                }}
              >
                Save Template
              </Button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-gray-500" />
            Module Visibility
          </h2>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Network className="w-3.5 h-3.5" />
            Wallet navigation toggles
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {TABS_CONFIG.map((tab) => {
            const Icon = tab.icon;
            const enabled = organization?.tabsEnabled?.includes(tab.key);

            return (
              <div
                key={tab.key}
                className={`rounded-xl border p-4 transition ${
                  enabled ? "border-blue-200 bg-blue-50/60" : "border-gray-200 bg-gray-50/60"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex p-2 items-center justify-center rounded-full ${
                        enabled ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{tab.title}</h3>
                      <p className="mt-1 text-xs text-gray-600 leading-relaxed">{tab.description}</p>
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={() => handleSwitchTab(tab.key, enabled)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 rounded-full bg-gray-300 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-full" />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
